import { Link } from "@/lib/page-rendering"
import { useTranslation } from "react-i18next"
import { LucideIcon, Send } from "lucide-react"
import type { VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"

export function SaveButton(props: React.ComponentProps<typeof Button>) {
  const { t } = useTranslation("common")
  return (
    <ActionButton variant="solid" {...props} Icon={Send} type="submit">
      {t("actions.save")}
    </ActionButton>
  )
}

// A page action's fixed size: 40px on every page and at every width, so the
// crumb row that carries it stays 56px. No colour or weight here: the
// variant owns both, and a stray text-* class would make tailwind-merge drop
// the variant's colour.
const actionClasses = "h-10 rounded-full px-6"

// The label is its own element so the crumb row can hide it from sight
// below `sm`, leaving the icon in a round control whose accessible name is
// still the label.
function ActionContent({
  Icon,
  children,
}: {
  Icon: LucideIcon
  children: React.ReactNode
}) {
  return (
    <>
      <Icon />
      <span data-slot="action-label">{children}</span>
    </>
  )
}

// A page action: `variant="solid"` for the page's one primary action, the
// default outline variant for the crumb row's secondary.
export function ActionButton({
  className,
  Icon,
  children,
  ...props
}: {
  Icon: LucideIcon
} & React.ComponentProps<typeof Button>) {
  return (
    <Button
      data-slot="action"
      {...props}
      className={cn(actionClasses, className)}
    >
      <ActionContent Icon={Icon}>{children}</ActionContent>
    </Button>
  )
}

// A page action that is a visit rather than a command: the same control as
// ActionButton, rendered as the host's link.
export function ActionLink({
  className,
  Icon,
  children,
  variant,
  ...props
}: {
  Icon: LucideIcon
} & React.ComponentProps<typeof Link> &
  Pick<VariantProps<typeof buttonVariants>, "variant">) {
  return (
    <Link
      data-slot="action"
      {...props}
      className={cn(buttonVariants({ variant }), actionClasses, className)}
    >
      <ActionContent Icon={Icon}>{children}</ActionContent>
    </Link>
  )
}
